import React from 'react';
import { Box, Flex, useBreakpointValue } from '@chakra-ui/react';
import { Modal, Button } from '@morressier/alembic';
import _ from 'lodash';

import { useForm } from 'react-hook-form';
import { FormFieldTagInput } from '.';
import { generateValidationSchema } from 'src/utils/validation';
import { FormFieldDefinition } from '@publishing-engine/domain/types/article';
import { ajvResolver } from '@hookform/resolvers/ajv';
import { TagInputForm } from './TagInputForm';
import { hasDuplicateKeywords } from 'src/utils/hasDuplicateKeywords';

export type ITagInputValue = {
  temp: { text: string }[];
  metadata: {
    [x: string]: string[];
  };
};

interface Props {
  isOpen: boolean;
  onSubmit: (value: ITagInputValue['metadata']) => void;
  onClose: () => void;
  formField: FormFieldTagInput;
  value: string[];
}

export const TagInputUpdateModal: React.FC<Props> = props => {
  const { isOpen, onSubmit, onClose, formField, value } = props;
  const modalWidth = useBreakpointValue({ base: '95vw', md: '755px' });

  const defaultValues = {
    ..._.set(
      {} as ITagInputValue,
      'temp',
      value.map(value => ({ text: value }))
    ),
    ..._.set({} as ITagInputValue, formField.caseDataFields.main, value)
  };

  const schema = generateValidationSchema({
    [formField.name]: formField
  } as Record<string, FormFieldDefinition>);

  const methods = useForm({
    defaultValues,
    resolver: ajvResolver(schema)
  });
  const { setValue, setError, getValues, clearErrors } = methods;
  const values = getValues('temp');

  const onSave = (data: ITagInputValue) => {
    if (hasDuplicateKeywords(values)) {
      return setError(formField.caseDataFields.main as any, {
        type: 'error',
        message: 'Duplicate values are not allowed'
      });
    } else {
      clearErrors(formField.caseDataFields.main as any);
    }
    onSubmit(data.metadata);
    onClose();
  };

  const onAllClear = () => {
    setValue('temp', []);
  };
  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {}}
      width={modalWidth}
      closeButtonProps={{
        hidden: true
      }}
    >
      <form onSubmit={methods.handleSubmit(onSave)}>
        <Flex
          padding={{ base: 4, md: 8 }}
          gap={6}
          bgColor="surfacePrimaryFill"
          flexDirection="column"
          borderRadius="base"
        >
          <Flex maxHeight={700} overflow={'auto'}>
            <TagInputForm config={formField} methods={methods} />
          </Flex>
          <Flex
            gap={2}
            justifyContent="space-between"
            alignItems="center"
            alignSelf="stretch"
            flexWrap={{ base: 'wrap', md: 'nowrap' }}
          >
            <Box>
              <Button
                variant="secondary"
                borderRadius="small"
                onClick={onAllClear}
              >
                Clear All
              </Button>
            </Box>
            <Box width={{ base: '100%', md: 'auto' }}>
              <Flex
                gap={2}
                alignItems="center"
                alignSelf="stretch"
                justifyContent={{ base: 'flex-end', md: 'flex-start' }}
              >
                <Button
                  variant="secondary"
                  onClick={onClose}
                  borderRadius="small"
                >
                  Exit Without Saving
                </Button>
                <Button variant="primary" type="submit" borderRadius="small">
                  Save Changes
                </Button>
              </Flex>
            </Box>
          </Flex>
        </Flex>
      </form>
    </Modal>
  );
};
